@let isImageLoaded = isImageLoaded$ | async;

<form
	direction="column"
	[formGroup]="formGroup"
	(ngSubmit)="handleSave()"
	horizontal="between"
	class="ds-h-sizes-height--full"
>
	<div matDialogTitle horizontal="between" vertical="center">
		My profile
		<button mat-icon-button>
			<eff-icon name="close" size="md" />
		</button>
	</div>
	<mat-dialog-content direction="column" class="ds-org-admin-my-profile__content">
		<div class="slds-p-around--xxx-small ds-scrollable--y ds-scrollable--compact">
			<div class="ds-org-admin-my-profile__organization">
				@if ((account$ | async)?.name; as accountName) {
					<div>
						{{ accountName }}
					</div>
				}
				@if (orgNumber) {
					<div class="ds-h-text-weight--bold slds-m-top--xx-small">Organization № {{ orgNumber }}</div>
				}
				@if (specialEdition) {
					<div>Edition: {{ specialEdition }}</div>
				}
			</div>
			<!-- Upload Your Photo -->
			<div
				class="slds-p-vertical--x-small slds-p-horizontal--xx-small slds-m-vertical--medium ds-org-admin-my-profile__upload-your-photo-action"
				(click)="handleUploadYourPhoto()"
			>
				<div vertical="center">
					<!-- Photo Upload Icon -->
					@if (isPhotoAdded) {
						@if (isImageLoaded) {
							<img
								class="ds-org-admin-my-profile__upload-your-photo-image"
								dsImage
								[src]="avatarUrl"
								(dsImageLoaded)="handleAvatarLoaded()"
								(dsImageFailed)="handleAvatarLoaded()"
								alt="Profile"
							/>
						} @else {
							<div
								class="ds-org-admin-my-profile__avatar-skeleton"
								[dsAnimation]="skeletonAnimation"
							></div>
						}
					} @else {
						@if (isImageLoaded) {
							<div
								class="ds-org-admin-my-profile__upload-your-photo__no-image slds-shrink-none"
								around="center"
							>
								{{ userInfoInitials }}
							</div>
						} @else {
							<div
								class="ds-org-admin-my-profile__upload-your-photo-image__skeleton"
								[dsAnimation]="skeletonAnimation"
							></div>
						}
					}
					<!-- / Photo Upload Icon -->
					<div class="slds-m-left--medium slds-p-left--xxx-small">
						<div class="ds-org-admin-my-profile__upload-your-photo-action-title">
							Change Profile Picture
						</div>
						<div class="slds-m-top--xx-small ds-org-admin-my-profile__upload-your-photo-action-advice">
							Add a personal touch to your profile.
						</div>
					</div>
				</div>
			</div>
			<!-- / Upload Your Photo -->

			<!-- First Name -->
			<div class="slds-m-bottom--medium">
				<mat-form-field
					appearance="fill"
					ds-color="light"
					size="large"
					class="ds-form-field--input"
					hideRequiredMarker
				>
					<mat-label>First Name</mat-label>
					<input
						#firstNameInput
						type="text"
						matInput
						required
						formControlName="firstName"
						[maxlength]="maxLengthField"
						dsDisableOnlySpacesValidator
					/>
				</mat-form-field>
				@if (!formGroup.controls.firstName.value) {
					<mat-error class="slds-p-horizontal--small ds-mat-error--label">
						{{ REQUIRED_VALIDATOR_ERROR }}
					</mat-error>
				}
			</div>
			<!-- / First Name -->

			<!-- Last Name -->
			<div class="slds-m-bottom--medium">
				<mat-form-field
					appearance="fill"
					ds-color="light"
					size="large"
					class="ds-form-field--input"
					hideRequiredMarker
				>
					<mat-label>Last Name</mat-label>
					<input
						#lastNameInput
						type="text"
						matInput
						required
						formControlName="lastName"
						[maxlength]="maxLengthField"
						dsDisableOnlySpacesValidator
					/>
				</mat-form-field>
				@if (!formGroup.controls.lastName.value) {
					<mat-error class="slds-p-horizontal--small ds-mat-error--label">
						{{ REQUIRED_VALIDATOR_ERROR }}
					</mat-error>
				}
			</div>
			<!-- / Last Name -->

			<!-- Email -->
			<div class="slds-m-bottom--medium">
				<mat-form-field appearance="fill" ds-color="light" size="large" class="ds-form-field--input">
					<mat-label>Email</mat-label>
					<input #emailInput type="text" matInput formControlName="email" maxlength="48" />
				</mat-form-field>
			</div>

			<!-- / Email -->

			<!-- Phone -->
			<div class="slds-m-bottom--medium">
				<mat-form-field appearance="fill" ds-color="light" size="large" class="ds-form-field--input">
					<mat-label>Phone</mat-label>
					<ds-tel-input
						[enablePlaceholder]="true"
						[enableSearch]="true"
						[formControl]="getPhoneFormControl()"
						#phone
						class="slds-m-top--small"
					/>
				</mat-form-field>
				@if (!formGroup.controls.phone.valid && formGroup.controls.phone.touched) {
					<mat-error class="slds-m-top--xxx-small slds-p-horizontal--small ds-mat-error--label">
						Invalid number for selected country
					</mat-error>
				}
			</div>
			<!-- / Phone -->

			<!-- Office -->
			<div class="slds-m-bottom--medium">
				<mat-form-field appearance="fill" ds-color="light" size="large" class="ds-form-field--select">
					<mat-label>Office</mat-label>
					<mat-select panelClass="ds-org-office-dialog__select-dropdown" formControlName="locationId">
						@for (type of userLocations; track type.value) {
							<mat-option class="eff-mat-option" [value]="type.value">
								{{ type.label }}
							</mat-option>
						}
					</mat-select>
				</mat-form-field>
			</div>
			<!-- / Office -->

			<!-- Job Title -->
			<div class="slds-m-bottom--medium">
				<mat-form-field appearance="fill" ds-color="light" size="large" class="ds-form-field--input">
					<mat-label>Job Title</mat-label>
					<input
						#jobTitleInput
						type="text"
						matInput
						formControlName="jobTitle"
						[maxlength]="maxLengthField"
					/>
				</mat-form-field>
			</div>
			<!-- / Job Title -->
			<!-- Default Project Role -->
			<mat-form-field appearance="fill" ds-color="light" size="large" class="ds-form-field--select">
				<mat-label> Default Project Role </mat-label>
				<eff-icon
					name="info"
					size="auto"
					class="slds-m-left--xxx-small ds-form-field--select-info-icon ds-my-profile__info-icon"
					matTooltip="Additional information about Default Project Role"
				/>
				<mat-select
					panelClass="ds-org-office-dialog__select-dropdown"
					formControlName="roles"
					multiple
					(closed)="onRolesPanelClosed()"
					(opened)="onRolesPanelOpened()"
					(selectionChange)="clearSearch()"
				>
					<!-- MENU SEARCH -->
					<div class="slds-p-bottom--small slds-m-top--medium slds-m-horizontal--small">
						<mat-form-field
							appearance="fill"
							ds-color="light"
							size="medium"
							class="ds-form-field--input"
							(click)="$event.stopPropagation()"
							[formGroup]="searchForm"
						>
							<!-- Prefix -->
							<span matPrefix class="ds-nested-menu__search-wrapper__search-icon">
								<eff-icon name="search" size="sm" class="ds-nested-menu__search-wrapper__icon"
							/></span>
							<!-- / Prefix -->

							<!-- Suffix -->
							@if ($any(searchForm.get('search')).value) {
								<button
									matSuffix
									mat-icon-button
									aria-label="Clear"
									(click)="clearSearch()"
									class="ds-button-rectangle--xs"
								>
									<eff-icon name="close" size="sm" />
								</button>
							}
							<!-- / Suffix -->

							<!-- Input -->
							<input
								#searchInput
								matInput
								type="text"
								[placeholder]="searchPlaceholder"
								formControlName="search"
								(keydown.space)="$event.stopPropagation()"
							/>
							<!-- / Input -->
						</mat-form-field>
					</div>
					<!-- MENU SEARCH -->
					@for (projectRole of roles; track projectRole) {
						<mat-option
							[style.display]="filteredRoles().includes(projectRole) ? 'flex' : 'none'"
							class="eff-mat-option"
							[value]="projectRole.value"
						>
							{{ projectRole.label }}
						</mat-option>
					}
				</mat-select>
			</mat-form-field>
			<!-- / Default Project Role -->

			<!-- Reset Password -->
			<div class="slds-m-top--small">
				<button class="ds-button ds-my-profile__button" type="button" (click)="handleResetPassword()">
					Reset Password
				</button>
			</div>
			<!-- / Reset Password -->

			<!-- Change Email -->
			<div class="slds-m-top--medium">
				<button class="ds-button ds-my-profile__button" type="button" (click)="handleChangeEmail()">
					Change Email
				</button>
			</div>
			<!-- / Change Email -->

			<!-- MCP API Key -->
			<div *dsPermissions="{ hidden: [Permissions.Mcp] }" class="slds-m-top--medium">
				@if (mcpRawKey) {
					<mat-form-field appearance="fill" ds-color="light" size="large" class="ds-form-field--input">
						<mat-label>MCP API Key</mat-label>
						<input matInput readonly [value]="mcpRawKey" />
						<button
							matSuffix
							mat-icon-button
							type="button"
							(click)="handleCopyMcpKey()"
							class="ds-button-rectangle--xs"
						>
							<eff-icon name="content-copy" [size]="14" />
						</button>
					</mat-form-field>
					<div class="ds-my-profile__mcp-key-warning">Save this key — it won't be shown again.</div>
				} @else if (mcpKeyPrefix) {
					<mat-form-field appearance="fill" ds-color="light" size="large" class="ds-form-field--input">
						<mat-label>MCP API Key</mat-label>
						<input matInput disabled [value]="mcpKeyPrefix + '...'" />
						<button
							matSuffix
							mat-icon-button
							type="button"
							(click)="handleRegenerateMcpKey()"
							class="ds-button-rectangle--xs"
						>
							<eff-icon name="refresh" [size]="14" />
						</button>
					</mat-form-field>
				} @else {
					<div>
						<button class="ds-button ds-my-profile__button" type="button" (click)="handleGenerateMcpKey()">
							Generate API Key
						</button>
					</div>
				}
			</div>
			<!-- / MCP API Key -->
		</div>
	</mat-dialog-content>
	<mat-dialog-actions horizontal="end">
		<button mat-stroked-button color="secondary" (click)="handleCancel()" type="button">Cancel</button>
		<button mat-raised-button color="primary" type="submit" class="slds-m-left--small">Save</button>
	</mat-dialog-actions>
</form>
